import React, { Fragment, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import RuleWizardDrawer from "./components/RuleWizardDrawer.jsx";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import { getMatrix, discoverSources, dismissDiscoveredSource, createRule } from "./api";
import { getActiveModelVersion, getCostModelNodes, getBudgetDimensions } from "../cost-allocation/api/costModelApi";

// Detected cost sources are assigned ownership in the Organisation hierarchy.
// Resolve the dimension by code at runtime — ids differ per environment, so hardcoding is fragile.
// (`organisation` is the canonical code; `org` is the pre-migration legacy code.)
const ORG_DIMENSION_CODES = ["organisation", "org"];

const euro = (n) => `€${Math.round(n || 0).toLocaleString()}`;

const DetectedCostSourcesPage = () => {
  const queryClient = useQueryClient();

  // ── data ─────────────────────────────────────────────────────────────────
  const { data, isLoading, error: loadError } = useQuery(
    ["alloc-matrix"],
    () => getMatrix(),
    { keepPreviousData: true }
  );

  // Resolve the Organisation ownership dimension (match by code; BudgetDimension may serialize PascalCase).
  const { data: dimensionsRaw } = useQuery(["budget-dimensions"], getBudgetDimensions);
  const orgDimension = useMemo(() => {
    const dims = (dimensionsRaw ?? []).map((d) => ({
      id: d.id ?? d.ID,
      code: (d.code ?? d.Code ?? "").toLowerCase(),
      name: d.name ?? d.Name,
    }));
    return dims.find((d) => ORG_DIMENSION_CODES.includes(d.code)) ?? null;
  }, [dimensionsRaw]);
  const ownerDimensionId = orgDimension?.id;

  const detected = (data?.sources || []).filter((s) => s.autoDiscovered);
  const unassigned = detected.filter((s) => !s.cells?.[ownerDimensionId]?.ownerName).length;

  // ── UI state ──────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingSource, setPendingSource] = useState(null);   // single assign
  const [dismissError, setDismissError] = useState(null);
  const [discoverResult, setDiscoverResult] = useState(null); // { created, skipped }
  const [discoverError, setDiscoverError] = useState(null);
  const [discoverLoading, setDiscoverLoading] = useState(false);

  // ── bulk picker state ─────────────────────────────────────────────────────
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkNodes, setBulkNodes] = useState([]);
  const [bulkOwnerId, setBulkOwnerId] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // ── handlers ──────────────────────────────────────────────────────────────
  const invalidate = () => queryClient.invalidateQueries(["alloc-matrix"]);

  const handleDiscover = async () => {
    setDiscoverLoading(true);
    setDiscoverResult(null);
    setDiscoverError(null);
    try {
      const res = await discoverSources();
      setDiscoverResult(res);
      invalidate();
    } catch {
      setDiscoverError("Discovery failed. Please try again.");
    } finally {
      setDiscoverLoading(false);
    }
  };

  const handleDismiss = async (source) => {
    setDismissError(null);
    try {
      // Dismiss only acknowledges the detection (clears auto_discovered) — it does NOT archive
      // the source, so it stays visible in the matrix and every other allocation view.
      await dismissDiscoveredSource(source.id);
      invalidate();
    } catch {
      setDismissError("Failed to dismiss source.");
    }
  };

  const handleAssignSingle = (source) => {
    setPendingSource(source);
  };

  const handleAssignBulk = async () => {
    if (selectedIds.size === 0) return;
    setBulkError(null);
    setBulkNodes([]);
    setBulkOwnerId(null);
    setBulkOpen(true);
    setBulkLoading(true);
    if (!ownerDimensionId) {
      setBulkError("Owner hierarchy not loaded yet. Please try again.");
      setBulkLoading(false);
      return;
    }
    try {
      const version = await getActiveModelVersion(ownerDimensionId);
      const versionId =
        version?.id ?? version?.ID ??
        version?.cost_model_version?.id ?? version?.cost_model_version?.ID;
      if (!versionId) {
        setBulkError("No active cost model for this hierarchy.");
        return;
      }
      const nodeList = (await getCostModelNodes(versionId)) || [];
      setBulkNodes(nodeList);
      if (nodeList.length > 0) {
        setBulkOwnerId(nodeList[0].node_id);
      }
    } catch {
      setBulkError("Failed to load owner nodes. Please try again.");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkApply = async () => {
    if (!bulkOwnerId || selectedIds.size === 0 || !ownerDimensionId) return;
    setBulkApplying(true);
    setBulkError(null);
    try {
      await Promise.all(
        [...selectedIds].map((id) =>
          createRule({
            sourceId: id,
            dimensionId: ownerDimensionId,
            ownerNodeId: bulkOwnerId,
            ruleType: "direct",
          })
        )
      );
      invalidate();
      setSelectedIds(new Set());
      setBulkOpen(false);
    } catch {
      setBulkError("Failed to assign owner. Please try again.");
    } finally {
      setBulkApplying(false);
    }
  };

  const handleBulkClose = () => {
    setBulkOpen(false);
    setBulkError(null);
  };

  const handleDrawerSaved = () => {
    setPendingSource(null);
    invalidate();
  };

  const handleDrawerClose = () => {
    setPendingSource(null);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === detected.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(detected.map((s) => s.id)));
    }
  };

  // ── columns ─────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        id: "select",
        Header: () => (
          <input
            type="checkbox"
            aria-label="Select all"
            checked={detected.length > 0 && selectedIds.size === detected.length}
            onChange={toggleSelectAll}
            className="accent-primary cursor-pointer align-middle"
          />
        ),
        Cell: ({ row }) => {
          const s = row.original;
          return (
            <input
              type="checkbox"
              aria-label={`Select ${s.displayName}`}
              checked={selectedIds.has(s.id)}
              onChange={() => toggleSelect(s.id)}
              onClick={(e) => e.stopPropagation()}
              className={`accent-primary cursor-pointer align-middle transition-opacity ${
                selectedIds.has(s.id) ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"
              }`}
            />
          );
        },
        width: 36,
        minWidth: 36,
        maxWidth: 36,
      },
      {
        id: "source",
        accessor: "displayName",
        Header: () => <span>Account / Source</span>,
        Cell: ({ value }) => <span className="font-medium text-text truncate">{value}</span>,
        flex: 1,
        minWidth: 220,
      },
      {
        id: "type",
        Header: () => <span>Type</span>,
        Cell: ({ row }) => (
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-text truncate">{row.original.typeName}</span>
            {row.original.categoryName && <span className="text-xs text-text-3 truncate">{row.original.categoryName}</span>}
          </div>
        ),
        width: 160,
        minWidth: 120,
        maxWidth: 220,
      },
      {
        id: "cost",
        accessor: "monthlyCost",
        align: "right",
        Header: () => <span>Cost/mo</span>,
        Cell: ({ value }) => <span className="tabular-nums text-text">{euro(value)}</span>,
        width: 110,
        minWidth: 90,
        maxWidth: 140,
      },
      {
        id: "owner",
        Header: () => <span>Owner</span>,
        Cell: ({ row }) => {
          const ownerName = row.original.cells?.[ownerDimensionId]?.ownerName || null;
          return <span className={ownerName ? "text-text" : "italic text-text-3"}>{ownerName || "Unassigned"}</span>;
        },
        width: 180,
        minWidth: 140,
        maxWidth: 240,
      },
      {
        id: "actions",
        align: "right",
        Header: () => null,
        Cell: ({ row }) => (
          <div className="flex items-center justify-end gap-4">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleAssignSingle(row.original); }}
              className="text-sm font-semibold text-primary hover:text-primary-hover"
            >
              Assign owner
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDismiss(row.original); }}
              className="text-xs font-medium text-text-3 hover:text-text-2"
            >
              Dismiss
            </button>
          </div>
        ),
        width: 190,
        minWidth: 170,
        maxWidth: 220,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detected, selectedIds, ownerDimensionId]
  );

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Fragment>
      <AdministrationHeader title="Detected Cost Sources" showPeriodSelector={false} />

      <div className="text-left flex flex-auto flex-col ml-10 mt-6 mr-6">

        {/* Error banner – dismiss failures */}
        {dismissError && (
          <div className="mb-4 px-4 py-2 text-sm text-danger bg-danger-soft border border-danger rounded-ctrl">
            {dismissError}
            <button
              type="button"
              onClick={() => setDismissError(null)}
              className="ml-3 underline text-danger hover:opacity-80"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Discover result banner */}
        {discoverResult && (
          <div className="mb-4 px-4 py-2 text-sm text-text bg-surface-2 border border-border rounded-ctrl flex items-center justify-between">
            <span>
              Discovery complete: {discoverResult.created} created · {discoverResult.skipped} skipped
            </span>
            <button
              type="button"
              onClick={() => setDiscoverResult(null)}
              className="ml-3 text-text-3 hover:text-text text-xs underline"
            >
              Close
            </button>
          </div>
        )}

        {/* Discover error banner */}
        {discoverError && (
          <div className="mb-4 px-4 py-2 text-sm text-danger bg-danger-soft border border-danger rounded-ctrl">
            {discoverError}
          </div>
        )}

        <TableCard
          title="Detected sources"
          subtitle={`${detected.length} detected · ${unassigned} unassigned`}
          primaryAction={{
            label: discoverLoading ? "Discovering…" : "Discover sources",
            onClick: handleDiscover,
            disabled: discoverLoading,
            icon: icon({ name: "magnifying-glass" }),
          }}
          controls={
            selectedIds.size > 0 ? (
              <button
                type="button"
                onClick={handleAssignBulk}
                className="text-[13px] font-medium text-primary hover:text-primary-hover"
              >
                Assign owner to {selectedIds.size} selected →
              </button>
            ) : null
          }
          error={loadError}
          errorMessage="Error loading cost sources"
          onRetry={invalidate}
        >
          {isLoading ? (
            // No in-body spinner: the global application loader (App.js ClipLoader) already
            // indicates fetching. Blank placeholder keeps the card height stable without a
            // second spinner or an empty-state flash on first load.
            <div className="min-h-[240px]" />
          ) : detected.length === 0 ? (
            <EmptyState
              icon={icon({ name: "magnifying-glass", style: "light" })}
              title="No detected sources"
              message="No auto-discovered cost sources found. Run discovery to scan for new sources."
              action={{ label: discoverLoading ? "Discovering…" : "Discover sources", onClick: handleDiscover }}
            />
          ) : (
            <FlexTable columns={columns} data={detected} getRowId={(s) => s.id} />
          )}
        </TableCard>
      </div>

      {/* Rule Wizard Drawer — single assign */}
      <RuleWizardDrawer
        open={pendingSource !== null}
        source={pendingSource}
        dimension={orgDimension}
        onClose={handleDrawerClose}
        onSaved={handleDrawerSaved}
      />

      {/* Bulk owner picker modal */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-card rounded-card border border-card-bd shadow-xl w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-text">
              Assign owner to {selectedIds.size} selected
            </h2>

            <div>
              <label className="block text-xs font-medium text-text-2 mb-1">Owner</label>
              {bulkLoading ? (
                <p className="text-xs text-text-3 italic">Loading owners…</p>
              ) : bulkError ? null : bulkNodes.length === 0 ? (
                <p className="text-xs text-text-3 italic">
                  No owner nodes available for this hierarchy.
                </p>
              ) : (
                <select
                  value={bulkOwnerId ?? ""}
                  onChange={(e) => setBulkOwnerId(Number(e.target.value))}
                  className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {bulkNodes.map((n) => (
                    <option key={n.node_id} value={n.node_id}>
                      {n.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {bulkError && (
              <p className="text-xs text-danger">{bulkError}</p>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleBulkClose}
                className="px-4 py-2 text-sm rounded-ctrl border border-border text-text hover:bg-surface-2 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkApply}
                disabled={bulkApplying || !bulkOwnerId}
                className="px-4 py-2 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {bulkApplying ? "Assigning…" : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default DetectedCostSourcesPage;
